import React from "react";

export const TIMESHEET_SPECIAL_JOB_TYPES = {
  1238: { name: "Отпуск без содержания", code: "БС" },
  1236: { name: "Больничный", code: "БЛ" },
  1237: { name: "Отпуск", code: "ОТ" },
  1891: { name: "Учебный отпуск", code: "УН" },
  1892: { name: "Отпуск по беременности и родам", code: "Р" },
  1893: { name: "Отпуск по уходу за ребенком", code: "Р" },
};

export const TIMESHEET_BUSINESS_TRIP_IDS = [
  1242, 1243, 1825, 1826, 1827, 1828, 1829, 1830, 1831,
];

export const isTimesheetOvertimeApproved = (report) => {
  if (!report.is_overtime || !report.overtime_statuses) return false;
  return Object.values(report.overtime_statuses).every(
    (status) => status === "Согласован",
  );
};

export const createTimesheetFormatCellValue =
  (formatTime) => (reports, isWeekend) => {
    if (!reports || reports.length === 0) {
      return isWeekend ? "ВЫХ" : "";
    }

    const specialReport = reports.find(
      (report) => TIMESHEET_SPECIAL_JOB_TYPES[report.portal_job_type?.id],
    );
    if (specialReport) {
      return TIMESHEET_SPECIAL_JOB_TYPES[specialReport.portal_job_type.id].code;
    }

    const hasBusinessTrip = reports.some((report) =>
      TIMESHEET_BUSINESS_TRIP_IDS.includes(report.portal_job_type?.id),
    );
    if (hasBusinessTrip) {
      return "К";
    }

    const totalHours = reports.reduce((sum, report) => {
      if (
        !TIMESHEET_SPECIAL_JOB_TYPES[report.portal_job_type?.id] &&
        !TIMESHEET_BUSINESS_TRIP_IDS.includes(report.portal_job_type?.id)
      ) {
        return sum + report.time_spent / 3600;
      }
      return sum;
    }, 0);

    return totalHours > 0 ? formatTime(totalHours) : "";
  };

export const classifyTimesheetDayReports = (
  dayReports,
  isWeekend,
  { formatTime, formatCellValue, isOvertimeApproved },
) => {
  const dayOffReports = dayReports.filter((report) => report.is_day_off);
  const dayOffHours = dayOffReports.reduce(
    (sum, report) => sum + report.time_spent / 3600,
    0,
  );
  const normalReports = dayReports.filter(
    (report) =>
      !report.is_overtime && !report.is_remote && !report.is_day_off,
  );
  const combinedRemoteOvertimeReports = dayReports.filter(
    (report) =>
      report.is_overtime && report.is_remote && isOvertimeApproved(report),
  );
  const remoteOnlyReports = dayReports.filter(
    (report) => !report.is_overtime && report.is_remote,
  );
  const overtimeOnlyReports = dayReports.filter(
    (report) =>
      report.is_overtime && !report.is_remote && isOvertimeApproved(report),
  );
  const remoteReports = dayReports.filter(
    (report) => !report.is_overtime && report.is_remote,
  );
  const overtimeReports = dayReports.filter(
    (report) => report.is_overtime && isOvertimeApproved(report),
  );

  const combinedHours = combinedRemoteOvertimeReports.reduce(
    (sum, report) => sum + report.time_spent / 3600,
    0,
  );
  const remoteOnlyHours = remoteOnlyReports.reduce(
    (sum, report) => sum + report.time_spent / 3600,
    0,
  );
  const overtimeOnlyHours = overtimeOnlyReports.reduce(
    (sum, report) => sum + report.time_spent / 3600,
    0,
  );
  const overtimeHours = overtimeReports.reduce(
    (sum, report) => sum + report.time_spent / 3600,
    0,
  );

  const specialReport = normalReports.find(
    (report) => TIMESHEET_SPECIAL_JOB_TYPES[report.portal_job_type?.id],
  );
  let specialClass = "";
  if (specialReport) {
    const code = TIMESHEET_SPECIAL_JOB_TYPES[specialReport.portal_job_type.id].code;
    if (code === "БЛ") specialClass = "cell-bl";
    if (code === "ОТ") specialClass = "cell-ot";
    if (code === "БС") specialClass = "cell-bs";
    if (code === "УН") specialClass = "cell-un";
    if (code === "Р") specialClass = "cell-r";
  }

  const normalCellValue = formatCellValue(normalReports, isWeekend);
  const remoteCellValue = formatCellValue(remoteReports, isWeekend);
  const hasBothTypes = normalReports.length > 0 && remoteReports.length > 0;
  const hasCombinedRemoteOvertime = combinedRemoteOvertimeReports.length > 0;
  const hasSeparateRemoteAndOvertime =
    remoteOnlyReports.length > 0 && overtimeOnlyReports.length > 0;
  const hasDayOffAndRemote =
    dayOffHours > 0 &&
    remoteOnlyHours > 0 &&
    normalReports.length === 0 &&
    overtimeHours === 0;
  const hasDayOffAndNormal =
    dayOffHours > 0 &&
    normalReports.length > 0 &&
    remoteReports.length === 0 &&
    overtimeHours === 0;
  const hasDayOffAndOvertime =
    dayOffHours > 0 &&
    overtimeHours > 0 &&
    normalReports.length === 0 &&
    remoteReports.length === 0;
  const hasDayOffNormalAndOvertime =
    dayOffHours > 0 &&
    normalReports.length > 0 &&
    overtimeHours > 0 &&
    remoteReports.length === 0;
  const hasThreeTypes =
    normalReports.length > 0 &&
    (remoteOnlyReports.length > 0 || combinedRemoteOvertimeReports.length > 0) &&
    (overtimeOnlyReports.length > 0 ||
      combinedRemoteOvertimeReports.length > 0);
  const hasThreeTypesButOnlyCombined =
    hasThreeTypes &&
    remoteOnlyReports.length === 0 &&
    overtimeOnlyReports.length === 0 &&
    combinedRemoteOvertimeReports.length > 0;
  const totalRemoteHoursForCell = remoteOnlyHours;
  const totalOvertimeHoursForCell = overtimeOnlyHours + combinedHours;
  const hasRemoteAndOvertimeDiagonal =
    totalOvertimeHoursForCell > 0 &&
    (totalRemoteHoursForCell > 0
      ? hasSeparateRemoteAndOvertime ||
        remoteOnlyReports.length > 0 ||
        overtimeOnlyReports.length > 0
      : hasCombinedRemoteOvertime);
  const hasSingleValueInDiagonal =
    totalRemoteHoursForCell === 0 && totalOvertimeHoursForCell > 0;
  const hasBusinessTrip = normalReports.some((report) =>
    TIMESHEET_BUSINESS_TRIP_IDS.includes(report.portal_job_type?.id),
  );
  const isEmptyCell =
    !normalCellValue &&
    !remoteCellValue &&
    !specialReport &&
    overtimeHours === 0 &&
    dayOffHours === 0;

  return {
    dayOffHours,
    normalReports,
    remoteReports,
    overtimeHours,
    specialReport,
    specialClass,
    normalCellValue,
    remoteCellValue,
    hasBothTypes,
    hasCombinedRemoteOvertime,
    hasDayOffAndRemote,
    hasDayOffAndNormal,
    hasDayOffAndOvertime,
    hasDayOffNormalAndOvertime,
    hasThreeTypes,
    hasThreeTypesButOnlyCombined,
    remoteOnlyHours,
    overtimeOnlyHours,
    combinedHours,
    totalRemoteHoursForCell,
    totalOvertimeHoursForCell,
    hasRemoteAndOvertimeDiagonal,
    hasSingleValueInDiagonal,
    hasBusinessTrip,
    isEmptyCell,
  };
};

export const getTimesheetDayCellClassName = (classification, dayData) => {
  const {
    specialClass,
    hasThreeTypes,
    hasThreeTypesButOnlyCombined,
    hasRemoteAndOvertimeDiagonal,
    hasSingleValueInDiagonal,
    hasCombinedRemoteOvertime,
    overtimeHours,
    hasBothTypes,
    remoteReports,
    hasDayOffAndRemote,
    hasDayOffAndNormal,
    hasDayOffAndOvertime,
    hasDayOffNormalAndOvertime,
    dayOffHours,
    hasBusinessTrip,
    isEmptyCell,
  } = classification;

  return `
      ${dayData.isWeekend ? "weekend" : ""}
      ${hasThreeTypes && !hasThreeTypesButOnlyCombined ? "cell-three-stripes" : ""}
      ${hasThreeTypesButOnlyCombined ? "cell-normal-plus-diagonal" : ""}
      ${hasDayOffNormalAndOvertime ? "cell-three-stripes cell-three-stripes-dayoff" : ""}
      ${hasRemoteAndOvertimeDiagonal && !hasThreeTypes && !hasDayOffNormalAndOvertime ? "cell-split-remote-overtime" : ""}
      ${hasRemoteAndOvertimeDiagonal && !hasThreeTypes && !hasDayOffNormalAndOvertime && hasSingleValueInDiagonal ? "cell-split-remote-overtime--single" : ""}
      ${hasCombinedRemoteOvertime && !hasRemoteAndOvertimeDiagonal && !hasThreeTypes && !hasDayOffNormalAndOvertime ? "cell-combined-remote-overtime" : ""}
      ${overtimeHours > 0 && !hasCombinedRemoteOvertime && !hasRemoteAndOvertimeDiagonal && !hasThreeTypes && !hasDayOffNormalAndOvertime ? "split-cell" : ""}
      ${(hasBothTypes || (overtimeHours > 0 && remoteReports.length > 0)) && !hasThreeTypes && !hasCombinedRemoteOvertime && !hasRemoteAndOvertimeDiagonal && !hasDayOffNormalAndOvertime ? "split-cell-remote" : ""}
      ${hasDayOffAndRemote ? "cell-split-dayoff-remote" : ""}
      ${hasDayOffAndNormal ? "cell-split-normal-dayoff" : ""}
      ${hasDayOffAndOvertime ? "cell-split-dayoff-overtime" : ""}
      ${dayOffHours > 0 && !hasDayOffAndRemote && !hasDayOffAndNormal && !hasDayOffAndOvertime && !hasDayOffNormalAndOvertime ? "dayoff-cell_true" : ""}
      ${remoteReports.length > 0 && !hasBothTypes && !hasDayOffAndRemote ? "remote-cell_true" : ""}
      ${specialClass}
      ${isEmptyCell ? "empty-cell" : ""}
      ${hasBusinessTrip ? "business-trip" : ""}
    `.trim();
};

export const TimesheetCategoryStripes = ({
  normalContent,
  remoteOnlyHours,
  combinedHours,
  overtimeOnlyHours,
  formatTime,
}) => (
  <div className="cell-three-stripes-inner">
    <div className="cell-stripe cell-stripe-normal">{normalContent}</div>
    {remoteOnlyHours > 0 ? (
      <div className="cell-stripe cell-stripe-remote">
        {formatTime(remoteOnlyHours)}
      </div>
    ) : null}
    {combinedHours > 0 ? (
      <div className="cell-stripe cell-stripe-remote-overtime">
        {formatTime(combinedHours)}
      </div>
    ) : null}
    {overtimeOnlyHours > 0 ? (
      <div className="cell-stripe cell-stripe-overtime">
        {formatTime(overtimeOnlyHours)}
      </div>
    ) : null}
  </div>
);

const wrapNotSelfEntered = (content, hasNotSelfEntered, className = "") => {
  if (!hasNotSelfEntered) {
    return content;
  }

  if (React.isValidElement(content) && content.type === "span") {
    return React.cloneElement(content, {
      className: [content.props.className, "not-self-entered"]
        .filter(Boolean)
        .join(" "),
    });
  }

  return <span className={`not-self-entered ${className}`.trim()}>{content}</span>;
};

export const TimesheetDayCellContent = ({
  classification,
  formatTime,
  hasNotSelfEntered = false,
}) => {
  const {
    specialReport,
    specialClass,
    hasThreeTypesButOnlyCombined,
    hasThreeTypes,
    hasRemoteAndOvertimeDiagonal,
    hasSingleValueInDiagonal,
    hasCombinedRemoteOvertime,
    hasDayOffAndRemote,
    hasDayOffAndNormal,
    hasDayOffAndOvertime,
    hasDayOffNormalAndOvertime,
    hasBothTypes,
    overtimeHours,
    normalCellValue,
    remoteCellValue,
    dayOffHours,
    remoteReports,
    remoteOnlyHours,
    overtimeOnlyHours,
    combinedHours,
    totalRemoteHoursForCell,
    totalOvertimeHoursForCell,
  } = classification;

  if (specialReport) {
    return (
      <div className={specialClass}>
        {wrapNotSelfEntered(
          TIMESHEET_SPECIAL_JOB_TYPES[specialReport.portal_job_type.id].code,
          hasNotSelfEntered,
        )}
      </div>
    );
  }

  if (hasThreeTypesButOnlyCombined) {
    return (
      <div className="cell-normal-plus-diagonal-inner">
        <div className="cell-normal-plus-diagonal-top">
          {wrapNotSelfEntered(normalCellValue, hasNotSelfEntered)}
        </div>
        <div
          className={`cell-normal-plus-diagonal-bottom cell-split-remote-overtime${hasSingleValueInDiagonal ? " cell-split-remote-overtime--single" : ""}`}
        >
          {totalRemoteHoursForCell > 0 && (
            <div className="split-remote-half">
              {formatTime(totalRemoteHoursForCell)}
            </div>
          )}
          <div className="split-overtime-half">
            {formatTime(totalOvertimeHoursForCell)}
          </div>
        </div>
      </div>
    );
  }

  if (hasThreeTypes) {
    return (
      <TimesheetCategoryStripes
        normalContent={wrapNotSelfEntered(normalCellValue, hasNotSelfEntered)}
        remoteOnlyHours={remoteOnlyHours}
        combinedHours={combinedHours}
        overtimeOnlyHours={overtimeOnlyHours}
        formatTime={formatTime}
      />
    );
  }

  if (hasDayOffNormalAndOvertime) {
    return (
      <div className="cell-three-stripes-inner">
        <div className="cell-stripe cell-stripe-normal">
          {wrapNotSelfEntered(normalCellValue, hasNotSelfEntered)}
        </div>
        <div className="cell-stripe cell-stripe-dayoff">
          {formatTime(dayOffHours)}
        </div>
        <div className="cell-stripe cell-stripe-overtime">
          {formatTime(overtimeHours)}
        </div>
      </div>
    );
  }

  if (hasRemoteAndOvertimeDiagonal) {
    return (
      <>
        {totalRemoteHoursForCell > 0 && (
          <div className="split-remote-half">
            {formatTime(totalRemoteHoursForCell)}
          </div>
        )}
        <div className="split-overtime-half">
          {formatTime(totalOvertimeHoursForCell)}
        </div>
      </>
    );
  }

  if (hasCombinedRemoteOvertime && !hasRemoteAndOvertimeDiagonal) {
    return (
      <div className="cell-combined-remote-overtime-value">
        {formatTime(combinedHours)}
      </div>
    );
  }

  if (hasDayOffAndRemote) {
    return (
      <>
        <div className="split-dayoff-half">{formatTime(dayOffHours)}</div>
        <div className="split-remote-half">{formatTime(remoteOnlyHours)}</div>
      </>
    );
  }

  if (hasDayOffAndNormal) {
    return (
      <>
        <div className="split-normal-half">
          {wrapNotSelfEntered(normalCellValue, hasNotSelfEntered)}
        </div>
        <div className="split-dayoff-half">{formatTime(dayOffHours)}</div>
      </>
    );
  }

  if (hasDayOffAndOvertime) {
    return (
      <>
        <div className="split-dayoff-half">{formatTime(dayOffHours)}</div>
        <div className="split-overtime-half">{formatTime(overtimeHours)}</div>
      </>
    );
  }

  if (hasBothTypes) {
    return (
      <>
        <div className="normal-hours">
          {wrapNotSelfEntered(normalCellValue, hasNotSelfEntered)}
        </div>
        <div className="remote-hours">{remoteCellValue}</div>
      </>
    );
  }

  if (overtimeHours > 0) {
    return (
      <div className="cell-overtime-remote-stack">
        {normalCellValue ? (
          <div className="normal-hours">
            {wrapNotSelfEntered(normalCellValue, hasNotSelfEntered)}
          </div>
        ) : null}
        {remoteReports.length > 0 ? (
          <div className="remote-hours">{remoteCellValue}</div>
        ) : null}
        <div className="overtime-hours">{formatTime(overtimeHours)}</div>
      </div>
    );
  }

  return (
    <span>
      {remoteReports.length > 0
        ? remoteCellValue
        : wrapNotSelfEntered(
            normalCellValue || (dayOffHours > 0 ? formatTime(dayOffHours) : ""),
            hasNotSelfEntered,
          )}
    </span>
  );
};

export const buildTimesheetDayCellTooltip = (
  dayData,
  classification,
  formatTime,
) => {
  let tooltipText = dayData.eventName || undefined;

  if (classification.overtimeOnlyHours > 0) {
    tooltipText = tooltipText
      ? `${tooltipText}\nПереработка: ${formatTime(classification.overtimeOnlyHours)}`
      : `Переработка: ${formatTime(classification.overtimeOnlyHours)}`;
  }

  if (classification.combinedHours > 0) {
    tooltipText = tooltipText
      ? `${tooltipText}\nУдаленная переработка: ${formatTime(classification.combinedHours)}`
      : `Удаленная переработка: ${formatTime(classification.combinedHours)}`;
  }

  if (classification.remoteOnlyHours > 0) {
    tooltipText = tooltipText
      ? `${tooltipText}\nУдаленно: ${formatTime(classification.remoteOnlyHours)}`
      : `Удаленно: ${formatTime(classification.remoteOnlyHours)}`;
  }

  if (classification.hasBusinessTrip) {
    tooltipText = tooltipText
      ? `${tooltipText}\nКомандировка`
      : "Командировка";
  }

  if (classification.specialReport) {
    const specialName =
      TIMESHEET_SPECIAL_JOB_TYPES[
        classification.specialReport.portal_job_type?.id
      ]?.name;
    if (specialName) {
      tooltipText = tooltipText
        ? `${tooltipText}\n${specialName}`
        : specialName;
    }
  }

  return tooltipText;
};

export const calculateTimesheetReportTotals = (
  reportsList,
  { isOvertimeApproved },
) => {
  let overtimeTotal = 0;
  let dayOffTotal = 0;
  const workingTotal = reportsList.reduce((sum, report) => {
    const hours = report.time_spent / 3600;
    if (report.is_overtime) {
      if (isOvertimeApproved(report)) {
        overtimeTotal += hours;
      }
      return sum;
    }
    if (report.is_day_off) {
      dayOffTotal += hours;
    }
    if (!TIMESHEET_SPECIAL_JOB_TYPES[report.portal_job_type?.id]) {
      return sum + hours;
    }
    return sum;
  }, 0);

  return { workingTotal, overtimeTotal, dayOffTotal };
};

export const hasTimesheetStatusColoring = (className) =>
  /dayoff-cell_true|split-cell|remote-cell_true|cell-split|cell-combined|cell-three|cell-bl|cell-ot|cell-bs|cell-un|cell-r|business-trip/.test(
    className,
  );

export const createTimesheetCellHelpers = (formatTime) => {
  const formatCellValue = createTimesheetFormatCellValue(formatTime);
  const isOvertimeApproved = isTimesheetOvertimeApproved;
  const deps = { formatTime, formatCellValue, isOvertimeApproved };

  return {
    formatCellValue,
    isOvertimeApproved,
    classifyDayReports: (dayReports, isWeekend) =>
      classifyTimesheetDayReports(dayReports, isWeekend, deps),
    getDayCellClassName: getTimesheetDayCellClassName,
    calculateReportTotals: (reportsList) =>
      calculateTimesheetReportTotals(reportsList, { isOvertimeApproved }),
    buildDayCellTooltip: (dayData, classification) =>
      buildTimesheetDayCellTooltip(dayData, classification, formatTime),
  };
};
